import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import FlippableCard from '@/components/FlippableCard';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import RegenerateButton from '@/components/RegenerateButton';
import { useAudio } from '@/hooks/useAudio';
import { getGifts, requestSpread, type ReadingRecord } from '@/api/client';
import { usePayment } from '@/hooks/usePayment';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Extended spread screen — Celtic Cross visualisation + paywall.
 *
 * Layout choices:
 *   The classic Celtic Cross has 10 positions in a tight cluster — too
 *   tight for small screens. We split it into TWO blocks vertically:
 *     • The cross (positions 1–6) on top
 *     • The staff (positions 7–10) below
 *   Each card has its label OUTSIDE the cluster (above for the cross, to
 *   the right of the staff) so nothing overlaps even on a 360px viewport.
 */

export default function Reading() {
  const { kind } = useParams<{ kind: string }>();
  const { t } = useTranslation();
  const audio = useAudio();

  type SpreadKind =
    | 'celtic_cross' | 'three_card' | 'love'
    | 'horseshoe' | 'magus_star' | 'wedding'
    | 'home' | 'masks' | 'wallet' | 'shadow' | 'crossroads';
  const spreadType = (kind as SpreadKind) ?? 'celtic_cross';
  // Each spread type has its own position labels — read from the
  // matching i18n array. Default to celtic_cross if key missing.
  const POSITION_KEY: Record<SpreadKind, string> = {
    celtic_cross: 'reading.positions',
    three_card:   'reading.positions_three_card',
    love:         'reading.positions_love',
    horseshoe:    'reading.positions_horseshoe',
    magus_star:   'reading.positions_magus_star',
    wedding:      'reading.positions_wedding',
    home:         'reading.positions_home',
    masks:        'reading.positions_masks',
    wallet:       'reading.positions_wallet',
    shadow:       'reading.positions_shadow',
    crossroads:   'reading.positions_crossroads',
  };
  const labels = (t(POSITION_KEY[spreadType], { returnObjects: true }) as string[]) || [];
  const PRODUCT_ID: Record<SpreadKind, string> = {
    celtic_cross: 'spread_celtic_cross',
    three_card:   'spread_three_card',
    love:         'spread_love',
    horseshoe:    'spread_horseshoe',
    magus_star:   'spread_magus_star',
    wedding:      'spread_wedding',
    home:         'spread_home',
    masks:        'spread_masks',
    wallet:       'spread_wallet',
    shadow:       'spread_shadow',
    crossroads:   'spread_crossroads',
  };
  const productId = PRODUCT_ID[spreadType];

  const PRICES: Record<SpreadKind, number> = {
    celtic_cross: 50, three_card: 25, love: 50,
    horseshoe: 25, magus_star: 30, wedding: 40,
    home: 25, masks: 25, wallet: 25, shadow: 30, crossroads: 25,
  };

  const { pay, paying, payError } = usePayment(productId);
  const { isPriestess } = useSubscription();
  const [unlocked, setUnlocked] = useState(false);
  const [reading, setReading] = useState<ReadingRecord | null>(null);
  const [spreadError, setSpreadError] = useState<string | null>(null);
  const [loadingFree, setLoadingFree] = useState(false);
  const [hasGift, setHasGift] = useState(false);

  // Check on mount whether the user has an unconsumed completed
  // transaction for this product (onboarding bonus, referral gift,
  // milestone reward). If yes, the button skips the payment flow.
  useEffect(() => {
    getGifts()
      .then((g) => setHasGift(g.products.includes(productId)))
      .catch(() => {});
  }, [productId]);

  useEffect(() => {
    audio.ambient(true);
    return () => audio.ambient(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSpread = async () => {
    setSpreadError(null);
    try {
      const r = await requestSpread({ spread_type: spreadType });
      audio.play('shuffle');
      setReading(r);
      setUnlocked(true);
    } catch (err) {
      setSpreadError((err as Error).message);
    }
  };

  const unlock = () => {
    // Three paths to a spread:
    //   1) Priestess subscription → free always
    //   2) Unconsumed gift transaction (onboarding/referral/milestone) → free
    //   3) Pay with Telegram Stars
    if (isPriestess || hasGift) {
      setLoadingFree(true);
      runSpread().finally(() => setLoadingFree(false));
      return;
    }
    pay(runSpread);
  };

  const title = t(`spread.${kind ?? 'celtic_cross'}`, { defaultValue: kind ?? '' });
  const cards = (reading?.cards ?? []) as any[];

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        {title}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t(`reading.subtitle_${spreadType}`, {
          defaultValue: t('reading.positions_subtitle'),
        })}
      </p>

      <OrnateFrame className="card-glow mb-6">
        {spreadType === 'celtic_cross' ? (
          <CelticCrossLayout cards={cards} labels={labels} t={t} />
        ) : (
          <LinearLayout
            cards={cards}
            labels={labels}
            count={labels.length || cards.length}
          />
        )}
      </OrnateFrame>

      {!unlocked && (
        <div className="text-center">
          <button
            className="btn-primary w-full"
            onClick={unlock}
            disabled={paying || loadingFree}
          >
            {paying || loadingFree
              ? t('payment.opening')
              : isPriestess
                ? t('spread.open_priestess')
                : hasGift
                  ? t('spread.open_gift')
                  : t('spread.buy_for_stars', { stars: PRICES[spreadType] ?? 50 })}
          </button>
          {/* Priestess gets unlimited access — the gift stays untouched until
              the subscription ends. We surface this so the user doesn't
              think their gift was just consumed. */}
          {isPriestess && hasGift && (
            <div className="mt-2 space-y-0.5 text-[10px] uppercase tracking-widest">
              <p className="text-gold-400/70">✦ {t('spread.included_priestess')}</p>
              <p className="text-emerald-300/80">🎁 {t('spread.gift_kept_for_later')}</p>
            </div>
          )}
          {isPriestess && !hasGift && (
            <p className="mt-2 text-[10px] uppercase tracking-widest text-gold-400/70">
              ✦ {t('spread.included_priestess')}
            </p>
          )}
          {!isPriestess && hasGift && (
            <p className="mt-2 text-[10px] uppercase tracking-widest text-emerald-300/80">
              🎁 {t('spread.gift_explain')}
            </p>
          )}
          {(payError || spreadError) && (
            <p className="mt-3 text-xs text-rose-400">
              {payError ?? spreadError}
            </p>
          )}
          <p className="mt-3 text-xs text-gold-300/50">
            {t('reading.spread_description')}
          </p>
        </div>
      )}

      {unlocked && reading?.interpretation && (
        <>
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="card-glow mt-2"
          >
            <h2 className="mb-3 font-serif text-lg text-gold-300">
              {t('reading.interpretation')}
            </h2>
            <RichText
              text={reading.interpretation}
              className="font-serif italic leading-relaxed text-gold-200"
            />
          </motion.section>

          {/* Priestess-only: regenerate the interpretation for the
              same cards. Useful when AI returns truncated or weird
              output on long spreads. Returns null for non-subscribers. */}
          {reading.id && (
            <RegenerateButton
              readingId={reading.id}
              onUpdated={setReading}
              className="mt-5"
            />
          )}

          {/* Post-reading navigation — pick another spread (left) or
              jump back to the top of this one (right). Both buttons
              share the same visual family as the header back button. */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <Link to="/spreads" className="nav-back-pill">
              <span>←</span>
              <span>{t('reading.choose_another')}</span>
            </Link>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label={t('reading.scroll_to_top')}
              className="nav-scroll-top"
            >
              ↑
            </button>
          </div>
        </>
      )}
    </main>
  );
}


/**
 * Celtic Cross uses a unique cross-shaped layout for positions 1-6 plus
 * a vertical staff for positions 7-10. We keep the layout but draw cards
 * as plain numbered tiles; labels live in the legend below to avoid
 * clipping long Russian words.
 */
function CelticCrossLayout({
  cards, labels, t,
}: {
  cards: any[];
  labels: string[];
  t: (k: string) => string;
}) {
  return (
    <>
      <div className="mb-6">
        <div className="mb-3 text-center text-[10px] uppercase tracking-widest text-gold-500/60">
          {t('reading.the_cross')}
        </div>
        <div className="relative mx-auto" style={{ height: 320, width: 280 }}>
          <CardSlot n={5} card={cards[4]}
            style={{ left: '50%', top: 0, transform: 'translateX(-50%)' }} />
          <CardSlot n={4} card={cards[3]}
            style={{ left: 0, top: '50%', transform: 'translateY(-50%)' }} />
          <CardSlot n={6} card={cards[5]}
            style={{ right: 0, top: '50%', transform: 'translateY(-50%)' }} />
          <CardSlot n={3} card={cards[2]}
            style={{ left: '50%', bottom: 0, transform: 'translateX(-50%)' }} />
          <CardSlot n={1} card={cards[0]}
            style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} />
          <CardSlot n={2} card={cards[1]} rotated
            style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%) rotate(90deg)' }} />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-baseline gap-1.5">
              <span className="text-gold-500/60">{i + 1}.</span>
              <span className="text-gold-300/80 leading-snug">{labels[i]}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-3 text-center text-[10px] uppercase tracking-widest text-gold-500/60">
          {t('reading.the_staff')}
        </div>
        <div className="mx-auto flex max-w-[260px] flex-col gap-3">
          {[9, 8, 7, 6].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <CardSlot n={i + 1} card={cards[i]} inline />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-gold-500/60">
                  {i + 1}.
                </div>
                <div className="text-sm text-gold-300">{labels[i]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * Generic layout for 3-card / 5-card spreads. Cards in a row (or wrap for
 * 5 cards), labels under each card. Clean, no crossing imagery.
 */
function LinearLayout({
  cards, labels, count,
}: {
  cards: any[];
  labels: string[];
  count: number;
}) {
  // For 5+ cards, wrap to 2 rows. For 3 cards, single row.
  const wrapClass = count <= 3 ? 'flex justify-center gap-3' : 'flex flex-wrap justify-center gap-3';
  return (
    <div>
      <div className={wrapClass}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex w-[110px] shrink-0 flex-col items-center text-center">
            <CardSlot n={i + 1} card={cards[i]} inline />
            <div className="mt-1.5 text-[10px] uppercase tracking-widest text-gold-500/60">
              {i + 1}.
            </div>
            <div className="text-[11px] leading-snug text-gold-300/80">
              {labels[i]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


interface SlotProps {
  n: number;
  card: any;
  rotated?: boolean;
  inline?: boolean;
  style?: React.CSSProperties;
}

function CardSlot({ n, card, inline, style }: SlotProps) {
  const empty = !card;
  const wrap = inline ? '' : 'absolute';
  // 76 × 102 — matches the deck's natural 768×1028 aspect (h/w ≈ 1.339).
  // Was 52 × 70 (same aspect, smaller) — bumped up so the gold filigree
  // and central artwork are actually legible on phone screens. Still
  // fits a 10-card Celtic Cross within max-w-md page width.
  const w = 76;
  const h = 102;

  return (
    <div className={wrap} style={style}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: n * 0.06 }}
        className="relative flex items-center justify-center overflow-hidden
                   rounded-lg border border-gold-500/40 bg-night-900/60"
        style={{ width: w, height: h }}
      >
        {empty ? (
          // Preview state (before purchase / reading drawn) — show the
          // deck's actual back illustration so the slot looks like a
          // stacked card, not an empty placeholder. The position number
          // sits as a small chip in the corner so position labels read
          // naturally without overwhelming the artwork.
          <>
            <img
              src="/cards/_back.jpeg"
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.85,
              }}
            />
            <span
              className="absolute right-1.5 top-1.5 rounded-full
                         border border-gold-500/60 bg-night-900/85
                         px-2 text-[11px] font-serif leading-tight text-gold-300"
            >
              {n}
            </span>
          </>
        ) : (
          // FlippableCard renders at native 160 px and we scale down to
          // fit the 76-wide slot (160 × 0.475 ≈ 76). Rendering big-then-
          // shrinking gives noticeably crisper gold filigree than letting
          // FlippableCard render at the slot size directly.
          <div style={{ transform: 'scale(0.475)', transformOrigin: 'center' }}>
            {/* Cards land face-down (back illustration), then auto-flip
                with a cascade so the reading feels like an actual deal.
                300ms per position gives a nice ripple from card 1 through
                card 10. Tapping a still-face-down card flips it early. */}
            <FlippableCard
              card={card}
              size={160}
              revealDelayMs={300 + (n - 1) * 300}
            />
          </div>
        )}
      </motion.div>
    </div>
  );
}
